import {
  Box,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Modal,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { borderRadius, styled } from '@mui/system'
import React, { useEffect, useMemo, useState } from 'react'
import { Form } from 'react-final-form'
import DropdownFieldSearch from '../../../../ui/DropdownFieldSearch'
import { CustomCircular, TextField } from '../../../../ui'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import { useEditCreditAppExpenseDataMutation, useLazyGetCreditAppExpenseDataQuery } from '../../../../services'
import { UspConfigurationCreditAppExpenseViewResult } from '../../../../models/apiTypes'

const classes = generateUtilityClasses('Box', ['root', 'row', 'buttonWrap', 'saveBtn'])

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.root}`]: {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '390px',
    backgroundColor: theme.palette.background.main,
    boxShadow: 24,
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
  },
  [`& .${classes.row}`]: {
    margin: theme.spacing(1, 0, 2, 0),
  },
  [`& .${classes.buttonWrap}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row-reverse',
    padding: theme.spacing(3, 0, 2, 0),
    marginTop: 'auto',
  },
  [`& .${classes.saveBtn}`]: {
    marginLeft: theme.spacing(2),
  },
}))

interface Props {
  rowData: UspConfigurationCreditAppExpenseViewResult
  open: boolean
  onClose: () => void
  type: string
  submit: (...args: any) => any
}

const EditApplicantExpensesModal = ({ open, onClose, rowData, type, submit }: Props) => {
  const [initialValues, setInitialValues] = useState({})
  const [editCreditAppExpense, { isLoading: isLoadingEditCreditAppExpense, isSuccess: isSuccessEditCreditAppExpense }] =
    useEditCreditAppExpenseDataMutation()
  const [getCreditAppExpenseData, { isLoading: isLoadingGetCreditAppExpense, isSuccess: isSuccessGetCreditAppExpense, data }] =
    useLazyGetCreditAppExpenseDataQuery()

  const handleSubmit = (values: any) => {
    if (type === 'edit') {
      editCreditAppExpense({
        creditAppExpenseId: rowData?.ID?.toString(),
        uspConfigurationCreditAppExpenseEdit: {
          expenseLabel: values.Description,
          isActive: values.IsActive,
        },
      })
    }
  }

  useEffect(() => {
    if (open && rowData && type === 'edit')
      getCreditAppExpenseData({
        creditAppExpenseId: rowData?.ID?.toString() as string,
      })
  }, [open, type, rowData])

  useEffect(() => {
    if (type === 'add') {
      setInitialValues({
        Description: '',
        IsActive: true,
      })
    }
    if (isSuccessGetCreditAppExpense && type === 'edit') {
      setInitialValues({
        Description: data?.ExpenseLabel,
        IsActive: data?.IsActive,
      })
    }
  }, [isSuccessGetCreditAppExpense, data, type])

  useEffect(() => {
    if (isSuccessEditCreditAppExpense) {
      onClose()
      submit()
    }
  }, [isSuccessEditCreditAppExpense])

  const isLoading = useMemo(
    () => isLoadingEditCreditAppExpense || isLoadingGetCreditAppExpense,
    [isLoadingEditCreditAppExpense, isLoadingGetCreditAppExpense],
  )

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Applicant Expenses</DialogTitle>
      <Form onSubmit={handleSubmit} initialValues={initialValues}>
        {({ handleSubmit }: any) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2} py={1}>
                <Grid item xs={12}>
                  <TextField required name='Description' label='Description' fullWidth />
                </Grid>

                <Grid item xs={12}>
                  <DropdownFieldSearch
                    name='IsActive'
                    label='Is Active?'
                    options={[
                      { key: true, values: 'Yes' },
                      { key: false, values: 'No' },
                    ]}
                    optionKey='key'
                    optionValue='values'
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <LoadingButton type='submit' className={classes.saveBtn} variant='contained'>
                Save
              </LoadingButton>
              <Button variant='contained' color='error' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </form>
        )}
      </Form>
      <CustomCircular open={isLoading} />
    </Dialog>
  )
}

export default EditApplicantExpensesModal
